/**
 * Core-100 R4 — CORE-AF "An event MUST NOT be silently lost ... failure to attach MUST surface".
 *
 * Every inbound text is retained in public.inbound_messages before it is routed. If placing it on a
 * timeline failed, the row stays 'failed' (a provider redelivery re-runs the routing). This banner makes
 * those messages visible to the front office instead of letting them disappear.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle } from 'lucide-react';

// inbound_messages is newer than the generated types: an untyped view of the SAME client.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const untyped = () => supabase as unknown as { from: (table: string) => any };

export function InboundRoutingFailures({ shopId }: { shopId: string | undefined }) {
  const failed = useQuery({
    queryKey: ['inbound-routing-failures', shopId],
    queryFn: async () => {
      const { count, error } = await untyped()
        .from('inbound_messages')
        .select('id', { count: 'exact', head: true })
        .eq('shop_id', shopId)
        .eq('routing_status', 'failed');
      if (error) throw error;
      return (count as number | null) ?? 0;
    },
    enabled: !!shopId,
    refetchInterval: 60_000,
  });
  if (!failed.data) return null;
  return (
    <div role="status" data-testid="inbound-routing-failures"
      className="mt-2 flex items-center gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-1 text-xs text-amber-800">
      <AlertTriangle className="h-3.5 w-3.5" />
      {failed.data} incoming message{failed.data === 1 ? '' : 's'} received and kept, but not yet placed on a conversation.
    </div>
  );
}
